import moment from 'moment';
import { MIT_SEGMENT_DATE_FORMAT, parseUtcGoalDate } from './mitDateUtils';
import { createOkrPeriodName, createQuarterIntervals, WbEventHandlerMixins } from '../../../../../utils/common';
import { displayFiscalYearFromPeriodRow } from '../../goalPeriodRows';
import {
  MIT_INTERVAL_TYPE,
  MIT_TYPE,
  MitIntervalType,
  MitType,
} from './mitConstants';

/** JS export defaults nestedFlow to boolean; callers pass mitType strings at runtime. */
const createQuarterIntervalsForMit = createQuarterIntervals as (
  type?: string,
  start_date?: string,
  target_date?: string,
  quarter_start_date?: string | number,
  nestedFlow?: MitType | string | boolean,
  orgPeriod?: unknown,
  orgReportingPeriods?: unknown,
) => MitDateSegment[];

export interface MitDateSegment {
  startDate: string;
  endDate: string;
  intervalName: string;
  quarterName?: string;
  year?: number | null;
}

export interface MetricQuarterOptionRow {
  period?: number | string;
  format?: string;
  start?: string;
  end?: string;
  name?: string;
  fiscalYear?: number | string;
  yearOffset?: number | string;
  year?: number | string;
}

export interface MitGoalDetails {
  isAnnualOkr?: boolean;
  start_date: string;
  target_date: string;
  metricQuarterOption?: MetricQuarterOptionRow[] | Record<string, MetricQuarterOptionRow>;
  filterOption?: unknown;
  orgReportingPeriods?: unknown;
}

export interface MitItem {
  id?: string | number;
  source?: string | number;
  type?: string | number;
  child_krs?: unknown[];
}

function momentFormatFromPhp(phpFormat?: string): string {
  if (!phpFormat) {
    return 'MM/DD/YYYY';
  }
  return phpFormat
    .replace(/Y/g, 'YYYY')
    .replace(/y/g, 'YY')
    .replace(/m/g, 'MM')
    .replace(/d/g, 'DD');
}

/**
 * True when the objective spans a configured org fiscal year (server-validated).
 */
export function isAnnualMitOkr(goalDetails?: MitGoalDetails | null): boolean {
  return Boolean(goalDetails?.isAnnualOkr);
}

/**
 * Default MIT child interval for new metrics: OKR start through end of that month,
 * capped at OKR end.
 */
export function createDefaultMitDateSegment(goalDetails: MitGoalDetails): MitDateSegment[] {
  const startUtc = parseUtcGoalDate(goalDetails.start_date);
  const targetUtc = parseUtcGoalDate(goalDetails.target_date);
  const endOfMonthUtc = startUtc.clone().endOf('month');
  const endUtc = endOfMonthUtc.isAfter(targetUtc) ? targetUtc : endOfMonthUtc;

  return [
    {
      startDate: startUtc.format(MIT_SEGMENT_DATE_FORMAT),
      endDate: endUtc.format(MIT_SEGMENT_DATE_FORMAT),
      intervalName: '',
    },
  ];
}

/**
 * Whether the MIT UI should auto-build default child intervals.
 * True for brand-new MITs, basic→MIT conversion, and when child_krs are stale
 * leftovers after MIT→basic (source is no longer MIT).
 * False only for an existing MIT parent that still has saved children.
 */
export function needsDefaultMitChildren(item?: MitItem | null): boolean {
  if (WbEventHandlerMixins.getKrType(item?.source, item?.type) !== 'multipleIntervalTargets') {
    return true;
  }
  return !Array.isArray(item?.child_krs) || item.child_krs.length === 0;
}

/** addUp, trackProgression, avgResults — fixed fiscal quarter child KRs on annual OKRs. */
export function isQuarterlyAggregationMitType(mitType?: MitType | string | null): boolean {
  return mitType === MIT_TYPE.ADD_UP
    || mitType === MIT_TYPE.TRACK_PROGRESSION
    || mitType === MIT_TYPE.AVG_RESULTS;
}

function withDateSegmentsFallback(
  segments: MitDateSegment[] | null | undefined,
  goalDetails: MitGoalDetails,
): MitDateSegment[] {
  if (!Array.isArray(segments) || segments.length === 0) {
    return createDefaultMitDateSegment(goalDetails);
  }
  return segments;
}

/**
 * MIT date segments from metricQuarterOption (goal FY quarters from server).
 */
export function dateSegmentsFromMetricQuarterOption(
  metricQuarterOption?: MitGoalDetails['metricQuarterOption'],
): MitDateSegment[] {
  const options = Array.isArray(metricQuarterOption)
    ? metricQuarterOption
    : Object.values(metricQuarterOption ?? {});

  return options
    .filter((option) => {
      const periodNum = Number(option.period);
      return periodNum >= 1 && periodNum <= 4;
    })
    .sort((a, b) => Number(a.period) - Number(b.period))
    .map((option) => {
      const format = momentFormatFromPhp(option.format);
      const displayYear = displayFiscalYearFromPeriodRow(option);
      const quarterLabel = option.name || `Q${option.period}`;
      return {
        startDate: moment.utc(option.start, format).format('MMM DD, YYYY'),
        endDate: moment.utc(option.end, format).format('MMM DD, YYYY'),
        intervalName: quarterLabel,
        quarterName: createOkrPeriodName(quarterLabel, displayYear, 1),
        year: displayYear,
      };
    });
}

/**
 * New metrics: metricQuarterOption. Existing / month / week: createQuarterIntervals.
 *
 * @param nestedFlow Legacy name — actually the MIT type string passed to createQuarterIntervals.
 */
export function resolveMitDateSegments(
  item: MitItem | null | undefined,
  goalDetails: MitGoalDetails,
  type: MitIntervalType | string = MIT_INTERVAL_TYPE.MONTH,
  start_date?: string,
  target_date?: string,
  quarter_start_date?: string | number,
  nestedFlow: MitType | string | boolean = false,
  orgPeriod?: unknown,
  orgReportingPeriods?: unknown,
): MitDateSegment[] {
  const fromMetricQuarters = dateSegmentsFromMetricQuarterOption(goalDetails?.metricQuarterOption);
  // Org fiscal quarters only when building defaults for annual OKRs with quarter intervals
  // (OKR-10775: never apply this to custom/month MIT init on quarter OKRs).
  if (
    fromMetricQuarters.length
    && needsDefaultMitChildren(item)
    && isAnnualMitOkr(goalDetails)
    && type === MIT_INTERVAL_TYPE.QUARTER
  ) {
    return fromMetricQuarters;
  }
  return createQuarterIntervalsForMit(
    type,
    start_date,
    target_date,
    quarter_start_date,
    nestedFlow,
    orgPeriod,
    orgReportingPeriods,
  );
}

/**
 * Initial child intervals when defaults are needed (new MIT or basic→MIT conversion):
 * fiscal quarters for annual quarter-aggregation modes, otherwise a single custom
 * interval (OKR start through end of that month). Existing MITs with child_krs return [].
 */
export function createInitialMitDateSegments(
  item: MitItem | null | undefined,
  goalDetails: MitGoalDetails,
  mitType: MitType | string | undefined,
  orgQuarterDates?: string | number,
  orgPeriod?: unknown,
  orgReportingPeriods?: unknown,
): MitDateSegment[] {
  if (!needsDefaultMitChildren(item)) {
    return [];
  }

  if (isQuarterlyAggregationMitType(mitType) && isAnnualMitOkr(goalDetails)) {
    return withDateSegmentsFallback(
      resolveMitDateSegments(
        item,
        goalDetails,
        MIT_INTERVAL_TYPE.QUARTER,
        goalDetails.start_date,
        goalDetails.target_date,
        orgQuarterDates,
        mitType,
        orgPeriod,
        orgReportingPeriods,
      ),
      goalDetails,
    );
  }

  // Custom / non-annual / unset mode: one month-bounded child.
  return createDefaultMitDateSegment(goalDetails);
}

export function initialMitIntervalMode(
  mitType: MitType | string | undefined,
  goalDetails: MitGoalDetails,
): MitIntervalType {
  if (isQuarterlyAggregationMitType(mitType) && isAnnualMitOkr(goalDetails)) {
    return MIT_INTERVAL_TYPE.QUARTER;
  }
  return MIT_INTERVAL_TYPE.CUSTOM;
}
